import type {
  MapCanvasInteractionState,
  MapCanvasShapesState,
  MapCanvasStagePositionState,
  MapCanvasStageState,
  MapCanvasState,
  MapCanvasValue,
  MapSaveOptions
} from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext/MapCanvasContext.types";
import { MapCanvasContext } from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext/useMapCanvas";
import { MapCanvasInteractionContext } from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext/useMapCanvasInteraction";
import { MapCanvasShapesContext } from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext/useMapCanvasShapes";
import { MapCanvasStageContext } from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext/useMapCanvasStage";
import { MapCanvasStagePositionContext } from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext/useMapCanvasStagePosition";
import type {
  AddImageFromFileOptions,
  AddSVGFromFileOptions,
  ClipboardPasteOptions,
  DuplicateShapeOptions,
  MapEditorActionsState,
  MapEditorPropsState,
  MapEditorSelectionState,
  MapEditorState,
  MapEditorToolState,
  MapEditorUIState
} from "@gettactic/map-builder/src/components/map/contexts/MapEditorContext/MapEditorContext.types";
import { MapEditorActionsContext } from "@gettactic/map-builder/src/components/map/contexts/MapEditorContext/useMapEditorActions";
import { MapEditorPropsContext } from "@gettactic/map-builder/src/components/map/contexts/MapEditorContext/useMapEditorProps";
import { MapEditorSelectionContext } from "@gettactic/map-builder/src/components/map/contexts/MapEditorContext/useMapEditorSelection";
import { MapEditorToolContext } from "@gettactic/map-builder/src/components/map/contexts/MapEditorContext/useMapEditorTool";
import { MapEditorUIContext } from "@gettactic/map-builder/src/components/map/contexts/MapEditorContext/useMapEditorUI";
import {
  readShapesFromSystemClipboard,
  writeShapesToSystemClipboard
} from "@gettactic/map-builder/src/components/map/editor/helpers/clipboardSerialize";
import { transformCompanions } from "@gettactic/map-builder/src/components/map/helpers/transformCompanions";
import type { MapBuilderProps } from "@gettactic/map-builder/src/MapBuilder";
import type { Shape, SpotShape } from "@gettactic/map-builder/src/types";
import {
  createCircle,
  createImageElement,
  createNodeShape,
  createRectangle,
  createSvgElement,
  createText
} from "@gettactic/map-builder/src/utils/shapeFactory";
import { loadSvgFile } from "@gettactic/map-builder/src/utils/svg";
import { MAP_BUILDER_VERSION } from "@gettactic/map-builder/src/version";
import type { LucideIcon } from "lucide-react";
import React from "react";
import ReactDOMServer from "react-dom/server";
import { useShallow } from "zustand/react/shallow";
import { alignShapes, distributeShapes } from "../document/alignOps";
import {
  addShapes,
  deleteShapes,
  duplicateShapes,
  reorderShapes,
  ungroupShape,
  updateShape as updateShapeOperation
} from "../document/operations";
import {
  documentFromShapes,
  migrateLoadedShapes,
  orderedShapes
} from "../document/serialize";
import { findShapeNode } from "../gesture/domTransform";
import { MAX_SCALE, MIN_SCALE } from "../gesture/useCanvasGestures";
import type { EditorStore } from "../store/createEditorStore";
import { useEditorSelector, useEditorStore } from "../store/EditorStoreContext";
import type { Viewport } from "../types";
import { useContainerSize } from "./useContainerSize";

/** Same inputs as the Konva `MapBuilder`, so the host switches with no
 *  prop changes. */
export type LegacyBridgeProps = MapBuilderProps & {
  /** The canvas container; fit/centre and locate use its size. */
  canvasRef: React.RefObject<HTMLDivElement | null>;
  /** The canvas svg; a few edits measure the rendered shape. */
  svgRef: React.RefObject<SVGSVGElement | null>;
};

const FIT_PADDING = 0.95;

const resolveAction = <T,>(action: React.SetStateAction<T>, current: T): T =>
  typeof action === "function"
    ? (action as (previous: T) => T)(current)
    : action;

/**
 * Provides the Konva editor's contexts from the SVG editor's store, so the
 * existing toolbars, panels, dialogs and shortcut hooks mount unchanged.
 * Everything the chrome reads is a store selector; everything it calls is
 * a store action, so the canvas keeps its per-shape subscriptions.
 */
export const LegacyContextBridge: React.FC<
  React.PropsWithChildren<LegacyBridgeProps>
> = ({ children, canvasRef, svgRef, ...props }) => {
  const store = useEditorStore();
  const shapesById = useEditorSelector((state) => state.shapes);
  const order = useEditorSelector((state) => state.order);
  const shapes = React.useMemo(
    () => orderedShapes({ shapes: shapesById, order }),
    [shapesById, order]
  );
  const shapesRef = React.useRef(shapes);
  shapesRef.current = shapes;

  const meta = useEditorSelector((state) => state.meta);
  const selection = useEditorSelector((state) => state.selection);
  const selectedIdsRef = React.useRef(selection);
  selectedIdsRef.current = selection;
  const viewport = useEditorSelector((state) => state.viewport);
  const stagePositionRef = React.useRef(viewport);
  stagePositionRef.current = viewport;
  const ui = useEditorSelector((state) => state.ui);
  const tool = useEditorSelector((state) => state.tool);
  const chrome = useEditorSelector((state) => state.chrome);
  const clipboard = useEditorSelector((state) => state.clipboard);
  const isDirty = useEditorSelector((state) => state.isDirty);
  const isDirtyRef = React.useRef(isDirty);
  isDirtyRef.current = isDirty;
  const { canUndo, canRedo } = useEditorSelector(
    useShallow((state) => ({
      canUndo: state.history.past.length > 0,
      canRedo: state.history.future.length > 0
    }))
  );
  const { width, height } = useContainerSize(canvasRef);

  const propsRef = React.useRef(props);
  propsRef.current = props;
  const shouldPreventDeselect = React.useRef(false);

  const fitViewport = React.useCallback((): Viewport | undefined => {
    const bounds = canvasRef.current?.getBoundingClientRect();
    if (!bounds || bounds.width === 0) return undefined;
    const { editingZoneWidth, editingZoneHeight } = store.getState().meta;
    const scale =
      Math.min(
        bounds.width / editingZoneWidth,
        bounds.height / editingZoneHeight
      ) * FIT_PADDING;
    const next = {
      x: bounds.width / 2 - (editingZoneWidth / 2) * scale,
      y: bounds.height / 2 - (editingZoneHeight / 2) * scale,
      scale
    };
    store.getState().setViewport(next);
    return next;
  }, [store, canvasRef]);

  const actions = React.useMemo<MapEditorActionsState>(
    () =>
      createActions(store, {
        propsRef,
        shouldPreventDeselect,
        selectedIdsRef,
        canvasRef,
        svgRef
      }),
    [store, canvasRef, svgRef]
  );

  const setShapes = actions.setShapes;

  const canvasValue = React.useMemo<MapCanvasState>(
    () => ({
      version: MAP_BUILDER_VERSION,
      gridSize: meta.gridSize,
      editingZoneWidth: meta.editingZoneWidth,
      editingZoneHeight: meta.editingZoneHeight,
      setGridSize: (gridSize) => store.getState().setMeta({ gridSize }),
      shapesRef,
      setShapes: (action) => setShapes(action),
      isDark: false,
      lastSpotLabel: meta.lastSpotLabel,
      setLastSpotLabel: (lastSpotLabel) =>
        store.getState().setMeta({ lastSpotLabel }),
      spotLabelPattern: meta.spotLabelPattern,
      setSpotLabelPattern: (action) =>
        store.getState().setMeta({
          spotLabelPattern: resolveAction(
            action,
            store.getState().meta.spotLabelPattern
          )
        }),
      compositeTemplates: meta.compositeTemplates ?? [],
      setCompositeTemplates: (action) =>
        store.getState().setMeta({
          compositeTemplates: resolveAction(
            action,
            store.getState().meta.compositeTemplates ?? []
          )
        }),
      zoneTypes: meta.zoneTypes ?? [],
      setZoneTypes: (action) =>
        store.getState().setMeta({
          zoneTypes: resolveAction(
            action,
            store.getState().meta.zoneTypes ?? []
          )
        }),
      pixelsPerMeter: meta.pixelsPerMeter ?? null,
      setPixelsPerMeter: (action) =>
        store.getState().setMeta({
          pixelsPerMeter: resolveAction(
            action,
            store.getState().meta.pixelsPerMeter ?? null
          )
        }),
      getCanvasValue: () => store.getState().getMapValue(),
      width,
      height,
      isViewMode: false,
      updateEditingZoneSize: (editingZoneWidth, editingZoneHeight) =>
        store.getState().setMeta({ editingZoneWidth, editingZoneHeight }),
      interactiveZones: props.interactiveZones,
      setHoveringNode: () => {},
      teamColors: props.teamColors,
      resourceId: props.resourceId,
      loadMapValueRecordsHistory: true,
      loadMapValue: (value: MapCanvasValue) => {
        // Replacing the map is one undoable edit that dirties the canvas,
        // unlike the initial load.
        const { shapes: loaded, ...rest } = value;
        const migrated = migrateLoadedShapes(loaded, value.version);
        store
          .getState()
          .commit(() => documentFromShapes(migrated), { selection: [] });
        store.getState().setMeta({
          gridSize: rest.gridSize,
          editingZoneWidth: rest.editingZoneWidth,
          editingZoneHeight: rest.editingZoneHeight,
          ...(rest.version && rest.lastSpotLabel
            ? { lastSpotLabel: rest.lastSpotLabel }
            : {}),
          ...(rest.version && rest.spotLabelPattern
            ? { spotLabelPattern: rest.spotLabelPattern }
            : {}),
          ...(rest.compositeTemplates
            ? { compositeTemplates: rest.compositeTemplates }
            : {}),
          ...(rest.zoneTypes ? { zoneTypes: rest.zoneTypes } : {}),
          pixelsPerMeter: rest.pixelsPerMeter ?? null
        });
      },
      fetchMapVersions: props.fetchMapVersions,
      activateMapVersion: props.activateMapVersion,
      onUploadIcon: props.onUploadIcon,
      canRebuildWithAi: props.canRebuildWithAi,
      mapIcons: props.mapIcons,
      apiCompositeTypes: props.apiCompositeTypes,
      saveCompositeType: props.saveCompositeType,
      updateCompositeType: props.updateCompositeType,
      deleteCompositeType: props.deleteCompositeType
    }),
    [
      store,
      setShapes,
      meta,
      width,
      height,
      props.interactiveZones,
      props.teamColors,
      props.resourceId,
      props.fetchMapVersions,
      props.activateMapVersion,
      props.onUploadIcon,
      props.canRebuildWithAi,
      props.mapIcons,
      props.apiCompositeTypes,
      props.saveCompositeType,
      props.updateCompositeType,
      props.deleteCompositeType
    ]
  );

  const stageValue = React.useMemo<MapCanvasStageState>(
    () => ({
      stagePositionRef,
      stageScale: viewport.scale,
      setStagePosition: (action) =>
        store
          .getState()
          .setViewport(resolveAction(action, store.getState().viewport)),
      updateStagePosition: (position) =>
        store
          .getState()
          .setViewport({ ...store.getState().viewport, ...position }),
      centerStage: fitViewport,
      minScale: MIN_SCALE,
      maxScale: MAX_SCALE
    }),
    [store, viewport.scale, fitViewport]
  );

  const stagePositionValue = React.useMemo<MapCanvasStagePositionState>(
    () => ({ stagePosition: viewport }),
    [viewport]
  );

  const shapesValue = React.useMemo<MapCanvasShapesState>(
    () => ({ shapes }),
    [shapes]
  );

  const interactionValue = React.useMemo<MapCanvasInteractionState>(
    () => ({
      selectionBox: null,
      setSelectionBox: () => {},
      selectionStart: null,
      setSelectionStart: () => {},
      isDragging: false,
      setIsDragging: () => {},
      shapesInSelection: [],
      setShapesInSelection: () => {},
      isAltPressed: false,
      setIsAltPressed: () => {},
      dragStartPositions: {},
      setDragStartPositions: () => {},
      minSelectionDistance: 5
    }),
    []
  );

  const selectionValue = React.useMemo<MapEditorSelectionState>(
    () => ({ selectedIds: selection }),
    [selection]
  );

  const toolValue = React.useMemo<MapEditorToolState>(
    () => ({
      activeTool: tool.kind,
      drawTool: {
        active: false,
        selectedShape: tool.drawShape,
        startPoint: null
      },
      iconToolSelectedIcon: chrome.iconToolSelectedIcon,
      furnitureToolSelectedFurniture: chrome.furnitureToolSelectedFurniture,
      zoneToolTag: chrome.zoneToolTag,
      hasRoomPicker: true
    }),
    [
      tool,
      chrome.iconToolSelectedIcon,
      chrome.furnitureToolSelectedFurniture,
      chrome.zoneToolTag
    ]
  );

  const clipboardValue = React.useMemo(
    () => ({ shapes: clipboard }),
    [clipboard]
  );

  const uiValue = React.useMemo<MapEditorUIState>(
    () => ({
      showOutside: ui.showOutside,
      snapToGrid: ui.snapToGrid,
      showSnapLines: ui.showSnapLines,
      showGrid: ui.showGrid,
      showZoneChecks: ui.showZoneChecks,
      showZones: ui.showZones,
      isPreview: chrome.isPreview,
      contextMenuShape: chrome.contextMenuShape,
      highlightedShape: chrome.highlightedShape,
      previewShape: null,
      nodeStyleToolTarget: chrome.nodeStyleToolTarget,
      canUndo,
      canRedo,
      clipboard: clipboardValue,
      wallPathSelectedVertexIndex: chrome.wallPathSelectedVertexIndex,
      isDirty,
      isDirtyRef
    }),
    [ui, chrome, canUndo, canRedo, clipboardValue, isDirty]
  );

  const propsValue = React.useMemo<MapEditorPropsState>(
    () => ({
      resources: props.resources,
      editingResources: props.editingResources,
      onExit: props.onExit,
      title: props.title,
      titlePrefix: props.titlePrefix,
      headerSlot: props.headerSlot,
      zoneEditor: props.zoneEditor,
      restrictedMode: props.restrictedMode,
      saveModes: props.saveModes,
      guidePoint: props.guidePoint
    }),
    [
      props.resources,
      props.editingResources,
      props.onExit,
      props.title,
      props.titlePrefix,
      props.headerSlot,
      props.zoneEditor,
      props.restrictedMode,
      props.saveModes,
      props.guidePoint
    ]
  );

  // The host reads the live editor state through `editorRef` (a function
  // in practice), the same way the Konva provider reports it every render.
  const editorRef = props.editorRef;
  if (editorRef) {
    const state: MapEditorState = {
      ...actions,
      ...selectionValue,
      ...toolValue,
      ...uiValue,
      ...propsValue
    };
    if (typeof editorRef === "function") {
      (editorRef as (value: MapEditorState) => void)(state);
    } else {
      editorRef.current = state;
    }
  }

  return (
    <MapCanvasStagePositionContext.Provider value={stagePositionValue}>
      <MapCanvasStageContext.Provider value={stageValue}>
        <MapCanvasShapesContext.Provider value={shapesValue}>
          <MapCanvasInteractionContext.Provider value={interactionValue}>
            <MapCanvasContext.Provider value={canvasValue}>
              <MapEditorActionsContext.Provider value={actions}>
                <MapEditorSelectionContext.Provider value={selectionValue}>
                  <MapEditorToolContext.Provider value={toolValue}>
                    <MapEditorUIContext.Provider value={uiValue}>
                      <MapEditorPropsContext.Provider value={propsValue}>
                        {children}
                      </MapEditorPropsContext.Provider>
                    </MapEditorUIContext.Provider>
                  </MapEditorToolContext.Provider>
                </MapEditorSelectionContext.Provider>
              </MapEditorActionsContext.Provider>
            </MapCanvasContext.Provider>
          </MapCanvasInteractionContext.Provider>
        </MapCanvasShapesContext.Provider>
      </MapCanvasStageContext.Provider>
    </MapCanvasStagePositionContext.Provider>
  );
};

interface ActionDependencies {
  propsRef: React.RefObject<MapBuilderProps>;
  shouldPreventDeselect: React.MutableRefObject<boolean>;
  selectedIdsRef: React.MutableRefObject<string[]>;
  canvasRef: React.RefObject<HTMLDivElement | null>;
  svgRef: React.RefObject<SVGSVGElement | null>;
}

const NATURAL_SIZE_CAP = 200;

/** The legacy action set, each one a thin call into the store. Built once
 *  per editor; every action reads the latest state when it runs. */
const createActions = (
  store: EditorStore,
  {
    propsRef,
    shouldPreventDeselect,
    selectedIdsRef,
    canvasRef,
    svgRef
  }: ActionDependencies
): MapEditorActionsState => {
  const state = () => store.getState();

  const setShapes = (
    action: React.SetStateAction<Shape[]>,
    allowHistoryUpdate = true
  ): void => {
    state().commit(
      (document) => {
        const current = orderedShapes(document);
        const next = resolveAction(action, current);
        return next === current ? document : documentFromShapes(next);
      },
      { history: allowHistoryUpdate }
    );
  };

  const add = (shape: Shape) => state().addShapes([shape]);

  const updateShape: MapEditorActionsState["updateShape"] = (
    id,
    newProps,
    allowHistoryUpdate = true
  ) => {
    const existing = state().shapes[id];
    let patch = newProps;
    const orientation = (newProps as Partial<SpotShape>).orientation;
    const isTurning =
      existing?.type === "spot" &&
      !!orientation &&
      orientation !== existing.orientation;
    if (
      isTurning &&
      existing.companions?.length &&
      !existing.compositeTemplateId
    ) {
      patch = {
        ...newProps,
        companions: transformCompanions(
          existing.companions,
          existing.width,
          existing.height,
          existing.orientation,
          orientation
        )
      } as Partial<Shape>;
    }
    // Flipping a desk top↔bottom moves its drawing inside the shape box;
    // shift the box so what is on screen stays where it was. Composites
    // keep their companions in place, so the shift would be wrong there.
    const isVerticalFlip =
      isTurning &&
      !existing.compositeTemplateId &&
      ((existing.orientation === "top" && orientation === "bottom") ||
        (existing.orientation === "bottom" && orientation === "top"));
    const node =
      isVerticalFlip && svgRef.current
        ? findShapeNode(svgRef.current, id)
        : null;
    if (node && existing) {
      const box = node.getBBox();
      const topPad = box.y - existing.y;
      const bottomPad = existing.y + existing.height - (box.y + box.height);
      patch = {
        ...patch,
        y: existing.y + (topPad - bottomPad)
      } as Partial<Shape>;
    }
    state().updateShape(id, patch, { history: allowHistoryUpdate });
  };

  const duplicateShape: MapEditorActionsState["duplicateShape"] = (
    id,
    options?: DuplicateShapeOptions
  ) => {
    const ids = Array.isArray(id) ? id : [id];
    const previousSelection = state().selection;
    const newIds = state().duplicateShapes(ids, {
      offset: options?.offset,
      override: options?.override,
      switchResourceId: options?.switchResourceId,
      switchIndex: options?.switchIndex
    });
    if (newIds.length === 0) return undefined;
    if (options?.selectDuplicates === false)
      state().setSelection(previousSelection);
    return newIds
      .map((newId) => state().shapes[newId])
      .filter((shape): shape is Shape => !!shape);
  };

  const pasteShapes = async (options?: ClipboardPasteOptions) => {
    const fromSystem = await readShapesFromSystemClipboard();
    const source = fromSystem ?? state().clipboard;
    if (source.length === 0) return undefined;
    const offset = 20;
    const scratch = documentFromShapes(source);
    const { document: pasted, newIds } = duplicateShapes(
      scratch,
      scratch.order,
      {
        offset: { x: offset, y: offset },
        override: options?.shapeOverrides
      }
    );
    const newShapes = newIds
      .map((newId) => pasted.shapes[newId])
      .filter((shape): shape is Shape => !!shape);
    state().commit((document) => addShapes(document, newShapes), {
      selection: newIds
    });
    return { newShapes, position: { x: offset, y: offset } };
  };

  return {
    selectedIdsRef,
    addShape: (position) => add(createRectangle(position)),
    addSvg: (position) => add(createSvgElement(position)),
    addSvgFromFile: async (file: string, options?: AddSVGFromFileOptions) => {
      const svgShape = options?.shape;
      try {
        const svgData = await loadSvgFile(file);
        const response = await fetch(svgData.blobUrl);
        const svgText = await response.text();
        const naturalWidth = svgData.width || 0;
        const naturalHeight = svgData.height || 0;
        let sourceWidth = svgShape?.width ?? naturalWidth;
        let sourceHeight = svgShape?.height ?? naturalHeight;
        // Caller sizes that fight the asset's aspect (square thumbnails on
        // a tall asset) snap to the natural aspect on the longest side.
        if (
          svgShape?.width &&
          svgShape?.height &&
          naturalWidth > 0 &&
          naturalHeight > 0
        ) {
          const naturalAspect = naturalWidth / naturalHeight;
          if (Math.abs(sourceWidth / sourceHeight - naturalAspect) > 0.05) {
            const target = Math.max(sourceWidth, sourceHeight);
            if (naturalAspect >= 1) {
              sourceWidth = target;
              sourceHeight = target / naturalAspect;
            } else {
              sourceHeight = target;
              sourceWidth = target * naturalAspect;
            }
          }
        }
        const longest = Math.max(sourceWidth, sourceHeight);
        const clamp =
          longest > NATURAL_SIZE_CAP ? NATURAL_SIZE_CAP / longest : 1;
        const newShape = createSvgElement(
          svgShape?.x || svgShape?.y
            ? { x: svgShape?.x, y: svgShape?.y }
            : undefined,
          options?.saveOriginalUrl ? file : svgData.blobUrl,
          options?.saveContent !== false ? svgText : undefined,
          sourceWidth * clamp,
          sourceHeight * clamp
        );
        newShape.layerType = svgShape?.layerType || "shapes";
        if (svgShape?.locked) newShape.locked = svgShape.locked;
        add(newShape);
        return { shapes: [newShape], svg: { ...svgData } };
      } catch {
        return undefined;
      }
    },
    addImageFromFile: async (
      file: string,
      options?: AddImageFromFileOptions
    ) => {
      const imageShape = options?.shape;
      try {
        const response = await fetch(file);
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        const size = await new Promise<{ width: number; height: number }>(
          (resolve, reject) => {
            const image = new Image();
            image.onload = () =>
              resolve({ width: image.width, height: image.height });
            image.onerror = reject;
            image.src = imageUrl;
          }
        );
        const newShape = createImageElement(
          imageShape?.x || imageShape?.y
            ? { x: imageShape?.x, y: imageShape?.y }
            : undefined,
          options?.saveOriginalUrl ? file : imageUrl,
          size.width,
          size.height
        );
        newShape.layerType = imageShape?.layerType || "shapes";
        if (imageShape?.locked) newShape.locked = imageShape.locked;
        add(newShape);
        return { shapes: [newShape], image: { ...size, blobUrl: "" } };
      } catch {
        return undefined;
      }
    },
    addIconSvg: (Icon: LucideIcon, position) => {
      const svgString = ReactDOMServer.renderToString(<Icon size={256} />);
      const blob = new Blob([svgString], { type: "image/svg+xml" });
      add(
        createSvgElement(position, URL.createObjectURL(blob), svgString, 60, 60)
      );
    },
    addCustomMultiShape: (position) => add(createNodeShape(position)),
    addText: (position) => add(createText(position)),
    updateShape,
    updateShapes: (ids, updated) =>
      state().commit((document) =>
        ids.reduce(
          (current, id) => updateShapeOperation(current, id, updated),
          document
        )
      ),
    duplicateShape,
    deleteShape: (id) => {
      const ids = Array.isArray(id) ? id : [id];
      state().commit((document) => deleteShapes(document, ids), {
        selection: state().selection.filter(
          (selected) => !ids.includes(selected)
        )
      });
    },
    moveShape: (id, direction) =>
      state().commit((document) => reorderShapes(document, [id], direction)),
    setShapes,
    setSelectedIds: (ids) => {
      state().setChrome({ highlightedShape: null });
      state().setSelection(ids);
    },
    toggleOutside: () =>
      state().setUi({ showOutside: !state().ui.showOutside }),
    setSnapToGrid: (snapToGrid) => state().setUi({ snapToGrid }),
    toggleSnapLines: () =>
      state().setUi({ showSnapLines: !state().ui.showSnapLines }),
    toggleGrid: () => state().setUi({ showGrid: !state().ui.showGrid }),
    setShowZoneChecks: (showZoneChecks) => state().setUi({ showZoneChecks }),
    setShowZones: (showZones) => state().setUi({ showZones }),
    openScalePrompt: () => state().setChrome({ pendingMeasurement: {} }),
    togglePreview: () =>
      state().setChrome({ isPreview: !state().chrome.isPreview }),
    alignSelection: (kind) =>
      state().commit((document) =>
        alignShapes(document, state().selection, kind)
      ),
    distributeSelection: (axis) =>
      state().commit((document) =>
        distributeShapes(document, state().selection, axis)
      ),
    setActiveTool: (kind) => state().setTool(kind),
    groupSelected: () => state().groupSelection(),
    ungroupShapes: (groupId) => {
      let childIds: string[] = [];
      state().commit((document) => {
        const result = ungroupShape(document, groupId);
        childIds = result.childIds;
        return result.document;
      });
      if (childIds.length === 0) return undefined;
      state().setSelection(childIds);
      return childIds
        .map((childId) => state().shapes[childId])
        .filter((shape): shape is Shape => !!shape);
    },
    setContextMenuShape: (contextMenuShape) =>
      state().setChrome({ contextMenuShape }),
    locateShape: (id) => {
      const shape = state().shapes[id];
      const bounds = canvasRef.current?.getBoundingClientRect();
      if (!shape || !bounds) return;
      const { scale } = state().viewport;
      state().setViewport({
        x: bounds.width / 2 - (shape.x + shape.width / 2) * scale,
        y: bounds.height / 2 - (shape.y + shape.height / 2) * scale,
        scale
      });
    },
    copySelectedShapes: () => {
      const { selection, shapes } = state();
      if (selection.length === 0) return undefined;
      state().copySelection();
      const copied = state().clipboard;
      void writeShapesToSystemClipboard(
        JSON.parse(JSON.stringify(copied)) as Shape[]
      );
      return { shapes: selection.map((id) => shapes[id]).filter(Boolean) };
    },
    pasteShapes,
    undo: () => state().undo(),
    redo: () => state().redo(),
    disableHistoryRecording: () => state().setHistoryRecording(false),
    enableHistoryRecording: () => state().setHistoryRecording(true),
    setHighlightedShape: (highlightedShape) =>
      state().setChrome({ highlightedShape }),
    setIconToolSelectedIcon: (iconToolSelectedIcon) =>
      state().setChrome({ iconToolSelectedIcon }),
    setFurnitureToolSelectedFurniture: (furnitureToolSelectedFurniture) =>
      state().setChrome({ furnitureToolSelectedFurniture }),
    setZoneToolTag: (zoneToolTag) => state().setChrome({ zoneToolTag }),
    // Drawing state lives inside the SVG tools; these legacy hooks are inert.
    handleDrawStart: () => {},
    handleDrawEnd: () => {},
    setDrawTool: (drawShape) => state().setTool(state().tool.kind, drawShape),
    setPreviewShape: () => {},
    shouldPreventDeselect,
    setEditingResources: (value) =>
      propsRef.current?.setEditingResources(value),
    setNodeStyleToolTarget: (nodeStyleToolTarget) =>
      state().setChrome({ nodeStyleToolTarget }),
    copyNodeStyleToNodeTarget: (source, target) => {
      const sourceShape = state().shapes[source];
      const targetShape = state().shapes[target];
      if (sourceShape?.type !== "spot" || targetShape?.type !== "spot") return;
      updateShape(target, {
        ...sourceShape,
        id: target,
        x: targetShape.x,
        y: targetShape.y,
        textProps: { ...sourceShape.textProps, text: sourceShape.label }
      });
    },
    save: async (options: MapSaveOptions) => {
      const result = await propsRef.current?.onSave(
        state().getMapValue(),
        options
      );
      if (result === false) return false;
      state().markSaved();
      return true;
    },
    setWallPathSelectedVertexIndex: (wallPathSelectedVertexIndex) =>
      state().setChrome({ wallPathSelectedVertexIndex })
  };
};

/** Circle is a draw-tool shape without its own factory options; kept for
 *  the shape-adding actions above. */
export const createCircleShape = createCircle;
